import React, { useState, useRef } from "react";
import {
  Layout,
  Typography,
  Input,
  Button,
  Card,
  Spin,
  Switch,
  ConfigProvider,
  theme,
  Space,
  Alert,
} from "antd";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./App.css";

const { Header, Content, Footer } = Layout;
const { Title, Paragraph } = Typography;
const { defaultAlgorithm, darkAlgorithm } = theme;

const App: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  // Reference for the PDF export container
  const resultRef = useRef<HTMLDivElement>(null);

  const toggleTheme = (checked: boolean) => {
    setIsDarkMode(checked);
  };

  const handleSearch = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError("");
    setResult("");

    try {
      const enrichedPrompt = `Act as an expert SEO Keyword Planner. Generate a comprehensive keyword strategy for the following topic: "${prompt}". Organize the output clearly using Markdown tables. Include columns for Keyword, Search Intent, and Estimated Difficulty.`;

      const response = await axios.post(
        "https://groqprompt.netlify.app/api/result",
        {
          prompt: enrichedPrompt,
        },
      );

      if (response.data && response.data.result) {
        setResult(response.data.result);
      } else {
        setError("Received an unexpected response format from the API.");
      }
    } catch (err) {
      setError("Failed to fetch keyword data. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!resultRef.current) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(resultRef.current, {
        scale: 2, // Enhances clarity for text
        useCORS: true,
        backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add subsequent pages if the content is long
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const safeFilename = prompt
        .trim()
        .replace(/[^a-z0-9]/gi, "-")
        .toLowerCase();
      pdf.save(`keywords-${safeFilename || "plan"}.pdf`);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Professional, friendly blue palette
  const primaryBlue = "#2f54eb";
  const bgLight = "#f0f5ff";
  const bgDark = "#0a0c10";

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
        token: {
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          colorPrimary: primaryBlue,
          borderRadius: 16,
          colorTextBase: isDarkMode ? "#e2e8f0" : "#334155",
        },
        components: {
          Layout: {
            headerBg: isDarkMode ? "#141414" : "#ffffff",
            bodyBg: isDarkMode ? bgDark : bgLight,
          },
          Card: {
            colorBgContainer: isDarkMode ? "#1f1f1f" : "#ffffff",
          },
        },
      }}
    >
      <Layout className="app-layout">
        {/* Navigation / Header */}
        <Header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 5% ",
            boxShadow: isDarkMode
              ? "0 4px 20px rgba(0,0,0,0.4)"
              : "0 4px 20px rgba(47, 84, 235, 0.08)",
            zIndex: 10,
            height: "80px",
          }}
        >
          <Space align="center" style={{ cursor: "pointer" }}>
            <span
              style={{ fontSize: "28px", marginRight: "8px" }}
              className="float-emoji"
            >
              ✨
            </span>
            <Title
              level={3}
              style={{
                margin: 0,
                color: isDarkMode ? "#ffffff" : primaryBlue,
                fontWeight: 800,
                letterSpacing: "-0.5px",
              }}
            >
              SOFT Keyword Planner
            </Title>
          </Space>
          <Space size="middle">
            <Switch
              checked={isDarkMode}
              onChange={toggleTheme}
              style={{ backgroundColor: isDarkMode ? primaryBlue : "#bae0ff" }}
            />
          </Space>
        </Header>

        {/* Main Content */}
        <Content
          style={{
            padding: "80px 20px",
            maxWidth: "960px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* Hero Section */}
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <Title
              level={1}
              style={{
                fontWeight: 800,
                letterSpacing: "-1px",
                fontSize: "3rem",
                marginBottom: "16px",
              }}
            >
              Discover your next big idea 🚀
            </Title>
            <Paragraph
              style={{
                fontSize: "20px",
                color: isDarkMode ? "#94a3b8" : "#64748b",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Enter a topic or niche, and let our AI craft a smart,
              comprehensive keyword plan just for you. 🪴
            </Paragraph>

            {/* Search Bar */}
            <Space.Compact
              style={{
                width: "100%",
                maxWidth: "650px",
                marginTop: "32px",
                boxShadow: isDarkMode
                  ? "0 8px 32px rgba(0,0,0,0.6)"
                  : "0 12px 36px rgba(47, 84, 235, 0.15)",
                borderRadius: "16px",
              }}
            >
              <Input
                size="large"
                placeholder="e.g., 'SaaS marketing' or 'Indoor gardening'"
                prefix={
                  <span style={{ marginRight: "10px", fontSize: "18px" }}>
                    💡
                  </span>
                }
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onPressEnter={handleSearch}
                style={{
                  padding: "16px 24px",
                  fontSize: "18px",
                  borderTopLeftRadius: "16px",
                  borderBottomLeftRadius: "16px",
                }}
              />
              <Button
                type="primary"
                size="large"
                onClick={handleSearch}
                loading={loading}
                style={{
                  height: "auto",
                  padding: "0 32px",
                  fontSize: "18px",
                  fontWeight: 700,
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {!loading && <span style={{ marginRight: "8px" }}>🔍</span>}{" "}
                Plan
              </Button>
            </Space.Compact>
          </div>

          {/* Error Handling */}
          {error && (
            <Alert
              message="Oops! 🙈"
              description={error}
              type="error"
              showIcon={false}
              style={{
                marginBottom: "32px",
                borderRadius: "16px",
                padding: "16px 24px",
              }}
            />
          )}

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin
                size="large"
                tip={
                  <span
                    style={{
                      marginTop: "24px",
                      display: "block",
                      fontWeight: 600,
                      fontSize: "18px",
                      color: primaryBlue,
                    }}
                  >
                    Brewing your keywords... ☕
                  </span>
                }
              />
            </div>
          )}

          {/* Results Area */}
          {result && !loading && (
            <Card
              bordered={false}
              style={{
                boxShadow: isDarkMode
                  ? "0 12px 48px rgba(0,0,0,0.5)"
                  : "0 12px 48px rgba(47, 84, 235, 0.08)",
                borderRadius: "24px",
                padding: "24px",
              }}
              // Added Header to the card for the Export Button
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    type="default"
                    onClick={handleDownloadPDF}
                    loading={isDownloading}
                    style={{
                      borderRadius: "8px",
                      fontWeight: 600,
                      borderColor: isDarkMode ? "#334155" : "#cbd5e1",
                    }}
                  >
                    {!isDownloading && (
                      <span style={{ marginRight: "6px" }}>📄</span>
                    )}
                    Export to PDF
                  </Button>
                </div>
              }
            >
              <div
                ref={resultRef}
                style={{
                  fontSize: "16px",
                  lineHeight: "1.8",
                  padding: "20px", // Added padding so the PDF content doesn't hug the edges tightly
                  backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff", // Ensures PDF picks up the right background
                  borderRadius: "12px",
                }}
                className="markdown-body"
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ node, children, ...props }) => (
                      <h1
                        style={{
                          color: primaryBlue,
                          borderBottom: `2px solid ${primaryBlue}`,
                          paddingBottom: "12px",
                          marginBottom: "24px",
                          fontWeight: 800,
                        }}
                        {...props}
                      >
                        {children}
                      </h1>
                    ),
                    h2: ({ node, children, ...props }) => (
                      <h2
                        style={{
                          color: isDarkMode ? "#f8fafc" : "#0f172a",
                          marginTop: "32px",
                          fontWeight: 700,
                        }}
                        {...props}
                      >
                        {children}
                      </h2>
                    ),
                    table: ({ node, ...props }) => (
                      <div
                        style={{
                          overflowX: "auto",
                          margin: "32px 0",
                          borderRadius: "12px",
                          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                        }}
                      >
                        <table
                          style={{ borderCollapse: "collapse", width: "100%" }}
                          {...props}
                        />
                      </div>
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        style={{
                          borderBottom: `2px solid ${isDarkMode ? "#334155" : "#cbd5e1"}`,
                          padding: "16px",
                          backgroundColor: isDarkMode ? "#1e293b" : "#f8fafc",
                          textAlign: "left",
                          fontWeight: 700,
                          color: isDarkMode ? "#f1f5f9" : "#334155",
                        }}
                        {...props}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        style={{
                          borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                          padding: "16px",
                        }}
                        {...props}
                      />
                    ),
                  }}
                >
                  {result}
                </ReactMarkdown>
              </div>
            </Card>
          )}
        </Content>

        {/* Footer */}
        <Footer
          style={{
            textAlign: "center",
            background: "transparent",
            color: isDarkMode ? "#64748b" : "#94a3b8",
            padding: "40px 50px",
            fontSize: "15px",
          }}
        >
          SOFT Keyword Planner © {new Date().getFullYear()} • Crafted warmly
          with 💙
        </Footer>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
